import { defineStore } from "pinia";
import {
  submitedDataFormatter,
  setDataValue,
  dataFormatterToSendServer,
} from "~/utils/helpers/ciFormatter";
import { getEnhancedPhoneNumber } from "~/utils/helpers/inputFormatter";
import { useToastStore } from "@/stores/toast";
import { useCookie } from "@/composable/useCookie";
import { useNuxtApp } from "#app";
import { convertDateToUnix } from "~/utils/helpers/dateTimeFormatter";
import type {
  IContactState,
  IState,
} from "~/types/contact/Types";
import { useContactFormStore } from "~/stores/contact/form"
import { useContactCallStore } from "~/stores/contact/call"
import { useFileManagerStore } from "~/stores/fileManager";

const baseUrl = import.meta.env["VITE_IPBX_URL"];

export const useContactStore = defineStore("ContactStore", {
  state: (): IContactState => ({
    submitedData: [],
    states: [],
    cities: [],
    totalPages: 0,
    totalItems: 0,
    isOpenForm: false,
    isOpenFilter: false,
    formData: {},
    searchData: {},
    searchFromDate: null,
    searchToDate: null,
    targetItem: {},
    add_drawer: false,
    searchTerm: "",
    contacts: [],
    notes: [],
    documents: [],
    attachments: [],
  }),

  getters: {
    formType: () => {
      const contactFormStore = useContactFormStore()
      return contactFormStore.targetForm?.type;
    },
    targetForm: () => {
      const contactFormStore = useContactFormStore()
      return contactFormStore.targetForm;
    },

    whatsappContacts: (state: IContactState) => {
      function hasWhatsapp(item: any) {
        if (item && item.whatsApp_phone && item.whatsApp_phone !== "") {
          return true;
        }
      }
      return state.submitedData.filter((x: any) => hasWhatsapp(x) === true).map((x: any) => ({ ...x, title: "" }));
    },
  },

  actions: {
    delete(id: string) {
      $fetch<any>(`/api/contact/data/remove`, {
        method: "POST",
        body: { id },
      });
    },

    async getSubmitedData(page: number = 1, count: number = 30) {
      if (!this.formType) return;
      const { query, create_time_period } = this.generateQueryParams()
      const params = {
        form_type: this.formType,
        page,
        query,
        page_size: count,
      }
      if (create_time_period) {
        params.create_time_period = create_time_period;
      }
      try {
        const data = await $fetch<any>(`/api/contact/data/list`, {
          method: "POST",
          body: params,
        });
        if (data) {
          this.submitedData = data.map((item: any, index: number) => {
            return {
              ...submitedDataFormatter(item),
              index: (page - 1) * count + index + 1
            };
          });
          return this.submitedData;
        } else {
          this.submitedData = [];
        }
      } catch (err: any) {
        console.log(err);
      }
    },

    async getSubmitedDataById(params: any) {
      try {
        const data = await $fetch<any>(`/api/contact/data/item`, {
          method: "POST",
          body: {
            ...params,
            service_type: "ci",
          },
        });
        this.targetItem = await setDataValue(data[0]);
        this.formData = this.targetItem.formData;
        this.notes = this.targetItem.notes;
        this.documents = this.targetItem.documents;
        this.attachments = this.targetItem.attachments;
        return submitedDataFormatter(data[0])
      } catch (err: any) {
        console.log(err);
      }
    },

    async getSubmitedDataPages() {
      if (!this.formType) return;
      const { query, create_time_period } = this.generateQueryParams()
      const params = {
        form_type: this.formType,
        query,
      }
      if (create_time_period) {
        params.create_time_period = create_time_period;
      }
      try {
        const data = await $fetch<number>(`/api/contact/data/count`, {
          method: "POST",
          body: params,
        });
        this.totalPages = data ? Math.ceil(data / 30) : 0;
        this.totalItems = data;
      } catch (err: any) {
        console.log(err);
      }
    },

    async searchSubmitedData(params: any) {
      const { create_time_period } = this.generateQueryParams()
      const info = {
        forms: [this.targetForm?.ulid],
        service_type: "ci",
        contains: params.contains,
      }
      if (create_time_period) {
        params.create_time_period = create_time_period;
      }
      try {
        const data = await $fetch<any>(`/api/contact/data/search`, {
          method: "POST",
          body: info,
        });
        if (data && data[params.type]) {
          this.submitedData = data[params.type].map(
            (item: any, index: number) => {
              return {
                index: index + 1,
                ...submitedDataFormatter(item),
              };
            }
          );
          this.totalPages = 1;
          this.totalItems = this.submitedData.length;
          return this.submitedData
        } else {
          this.submitedData = [];
        }
      } catch (err: any) {
        console.log(err);
      }
    },

    async searchByForm(param: any, form_type: string) {
      const data = {
        form_type: form_type,
        query: {
          "values.call_id": param,
        },
      };
      const res = await $fetch("/api/contact/data/searchByForm", {
        method: "POST",
        body: data,
      });

      return res;
    },

    async submitData(params: any, isLink: boolean) {
      const toastStore = useToastStore();
      try {
        const data = await $fetch<any>(`/api/contact/data/submit`, {
          method: "POST",
          body: params,
        });
        if (data.ulid) {
          const info = {
            ...params,
            ulid: data.ulid,
          };
          if (isLink) return info;
          this.submitedData.unshift({
            ...submitedDataFormatter(info),
          });
          this.updateIndexes();
          this.totalItems++
          toastStore.showAlert({
            title: "Contact Insert Successfully",
            showCloseButton: true,
            timerProgressBar: true,
          });
        } else if (data.error) {
          throw new Error(data.error);
        }
      } catch (err: any) {
        let errorText = "Can Not Connect To Server";
        if (err instanceof Error) {
          errorText = err.message;
        }
        toastStore.showAlert({
          title: errorText,
          showCloseButton: true,
          timerProgressBar: true,
          type: "error"
        });
        throw err;
      }
    },

    updateIndexes() {
      this.submitedData.forEach((item, idx) => {
        item.index = idx + 1;
      });
    },

    async updateData() {
      const nuxtApp = useNuxtApp();
      const router = nuxtApp.$router;
      const toastStore = useToastStore();
      const fields = dataFormatterToSendServer(
        this.formData,
        this.targetForm,
        true
      );
      const userId = router.currentRoute._value.params.id;
      const params = {
        form_type: this.formType,
        values: fields,
        ulid: userId,
        service_type: "ci",
      };
      try {
        const data = await $fetch<any>(`/api/contact/data/update`, {
          method: "PUT",
          body: params,
        });
        if (data.error) {
          toastStore.showAlert({
            title: data.error,
            showCloseButton: true,
            timerProgressBar: true,
            type: "error"
          });
          return
        }

        ["phone_number", "whatsApp_phone", "telegram_phone", "nava_phone"].forEach(key => {
          if (this.formData[key] && !this.formData[key].startsWith('98')) {
            const value = this.formData[key];
            if (/^(09|9)/.test(value)) {
              this.formData[key] = value.replace(/^(09|9)/, "989");
            } else {
              this.formData[key] = value.replace(/^(?:\+98|0)/, "98");
            }
          }
        });
        if (this.formData.telephone) {
          const value = this.formData.telephone;
          this.formData.telephone = value[0] == 0 ? `98${value.slice(1)}` : value;
        }

        this.targetItem.formData = { ...this.formData }
      } catch (err: any) {
        toastStore.showAlert({
          title: "Can Not Connect To Server",
          showCloseButton: true,
          timerProgressBar: true,
          type: "error"
        });
        console.log(err);
      }
    },

    async uploadFile() {
      const fileManagerStore = useFileManagerStore();
      if (!this.formData.Avatar) {
        this.formData.avatarUrl = ""
      }
      if (typeof this.formData.Avatar === 'object') {
        const fileUpload = await fileManagerStore.uploadFile(this.formData.Avatar);
        this.formData.avatarUrl = URL.createObjectURL(this.formData.Avatar)
        this.formData.Avatar = fileUpload;
      }
    },

    async getConnections(id: string) {
      try {
        const connections = await $fetch<any>(`/api/contact/data/connections`, {
          method: "POST",
          body: { id },
        });
        if (connections.length) {
          const data = await $fetch<any>(`/api/contact/data/item`, {
            method: "POST",
            body: {
              ulid_list: connections,
              service_type: "ci",
            },
          });
          if (data) {
            this.contacts = data.map((item: any) => {
              return {
                ...submitedDataFormatter(item),
              };
            });
            return;
          } else {
            this.contacts = [];
          }
        }
      } catch (err: any) {
        console.log(err);
      }
    },

    async updateContact(params: any) {
      const toastStore = useToastStore();
      try {
        const data = await $fetch<any>(`/api/contact/data/update`, {
          method: "PUT",
          body: {
            service_type: "ci",
            ...params,
          },
        });
        if (data.error) {
          throw new Error(data.error);
        }
        const contact = this.contacts.find(
          (item: any) => item.ulid == params.ulid
        );
        if (contact) {
          Object.assign(contact, params);
        }
        toastStore.showAlert({
          title: "Contact Update Successfully",
          showCloseButton: true,
          timerProgressBar: true,
        });
      } catch (err: any) {
        let errorText = "Can Not Connect To Server";
        if (err instanceof Error) {
          errorText = err.message;
        }
        toastStore.showAlert({
          title: errorText,
          showCloseButton: true,
          timerProgressBar: true,
          type: "error"
        });
        throw err;
      }
    },

    async connectUser(params: any) {
      const toastStore = useToastStore();
      try {
        const data = await $fetch<any>(`/api/contact/data/connect`, {
          method: "POST",
          body: {
            from_ulid: params.ulid,
            to_ulid: params.userId,
          },
        });
        this.contacts.unshift({
          ...submitedDataFormatter(params),
        });
        toastStore.showAlert({
          title: "Contact Insert Successfully",
          showCloseButton: true,
          timerProgressBar: true,
        });
      } catch (err: any) {
        let errorText = "Can Not Connect To Server";
        toastStore.showAlert({
          title: errorText,
          showCloseButton: true,
          timerProgressBar: true,
          type: "error"
        });
      }
    },

    async disconnectUser(params: any) {
      const toastStore = useToastStore();
      try {
        const data = await $fetch<any>(`/api/contact/data/disconnect`, {
          method: "POST",
          body: params.data
        });
        const findIndex = this.contacts.findIndex((item) => item.ulid == params.ulid);
        if (findIndex == -1) return;
        this.contacts.splice(findIndex, 1);
        toastStore.showAlert({
          title: "Contact Delete Successfully",
          showCloseButton: true,
          timerProgressBar: true,
        });
      } catch (err: any) {
        let errorText = "Can Not Connect To Server";
        toastStore.showAlert({
          title: errorText,
          showCloseButton: true,
          timerProgressBar: true,
          type: "error"
        });
      }
    },

    async updateList(params: any) {
      const toastStore = useToastStore();
      try {
        const data = await $fetch<any>(`/api/contact/data/update`, {
          method: "PUT",
          body: {
            service_type: "ci",
            ...params.data
          },
        });
        if (data.error) {
          throw new Error(data.error);
        }
        toastStore.showAlert({
          title: params.text,
          showCloseButton: true,
          timerProgressBar: true,
        });
      } catch (err: any) {
        let errorText = "Can Not Connect To Server";
        if (err instanceof Error) {
          errorText = err.message;
        }
        toastStore.showAlert({
          title: errorText,
          showCloseButton: true,
          timerProgressBar: true,
          type: "error"
        });
        throw err;
      }
    },

    async downloadFile(path: string) {
      const { getCookie } = useCookie();
      const cookie = getCookie("accessToken");
      const res: any = await $fetch(`${baseUrl}/download/${path}`, {
        method: "GET",
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${cookie}`,
        },
      });
      // if (res) {
      //   const url = URL.createObjectURL(res);
      //   this.audioSources[id] = url
      //   this.loading[id] = false
      //   addItemToIndexDB("files", {
      //     id,
      //     file: res,
      //   });
      // }
    },

    async getState() {
      try {
        const data = await $fetch<IState[]>(`/api/contact/state`);
        this.states = data ?? [];
      } catch (err: any) {
        console.log(err);
      }
    },

    async getCity(id: string = "") {
      try {
        if (!id) {
          this.cities = [];
          return;
        }
        const data = await $fetch<string[]>("/api/contact/city", {
          method: "POST",
          body: { id },
        });
        this.cities = data;
      } catch (err: any) {
        console.log(err);
      }
    },

    resetSearchFilter() {
      this.searchData = {};
      this.searchFromDate = null;
      this.searchToDate = null;
      this.cities = [];
    },
    resetFormData() {
      this.formData = { ...this.targetItem.formData };
    },
    emptyFormData() {
      const contactCallStore = useContactCallStore()
      this.formData = {};
      this.contacts = [];
      this.notes = [];
      this.attachments = [];
      this.documents = [];
      contactCallStore.resetData()
    },

    openContactForm(type: string) {
      this.add_drawer = true;
    },
    closeContactForm() {
      this.add_drawer = false;
    },

    async importFile(params: any) {
      const toastStore = useToastStore();

      const formData = new FormData();
      formData.append("file", params.file);
      formData.append("type", params.type);

      try {
        const data = await $fetch<any>(`/api/contact/data/import`, {
          method: "POST",
          body: formData
        });
        // toastStore.showAlert({
        //   title: "Recording Upload Successfully",
        //   showCloseButton: true,
        //   timerProgressBar: true,
        // });
        return true
      } catch (err: any) {
        let errorText = "Can Not Connect To Server";
        toastStore.showAlert({
          title: errorText,
          showCloseButton: true,
          timerProgressBar: true,
          type: "error"
        });
      }

    },

    async exportFile() {
      const { query, create_time_period } = this.generateQueryParams()
      const params = {
        form_type: this.formType,
        query,
      }
      if (create_time_period) {
        params.create_time_period = create_time_period;
      }

      try {
        const response = await fetch(`/api/contact/data/export`, {
          method: "POST",
          body: JSON.stringify(params),
          headers: { "Content-Type": "application/json" },
        });

        if (!response) return;

        const filename = response.headers.get("X-Filename") || "report.xls";
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        const element = document.createElement("a");
        element.href = url;
        element.download = filename;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);

      } catch (err: any) {
        console.log(err);
      }
    },

    generateQueryParams() {
      const query = {}
      Object.entries(this.searchData).forEach(([key, value]) => {
        if (value) {
          query[`values.${key}`] = value;
        }
      });
      const from = this.searchFromDate ? convertDateToUnix(this.searchFromDate) : 0;
      const to = this.searchToDate
        ? convertDateToUnix(this.searchToDate, true)
        : 0;

      const date = from || to ? [+from, +to] : null;
      return {
        query,
        create_time_period: date
      };
    },
    setIsSlected(item: any) {
      const selectedContactIndex = this.submitedData.findIndex(
        (x) => x.ulid === item.ulid
      );

      this.submitedData[selectedContactIndex].isSelected = true
    },
    clearIsSelected() {
      this.submitedData.forEach((x) => (x.isSelected = false));
    }
  },
});
